import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useSubscriptionCheck } from './hooks/useSubscriptionCheck';
import { toast } from 'react-hot-toast';
import { fetchWithCSRF } from './utils/csrf';
import { getFirstTouchQueryString } from './utils/attribution';

export default function SignUp() {
  const { isAuthenticated, isLoading: authLoading, loginWithPopup } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { hasSubscription, isLoading: subscriptionLoading } = useSubscriptionCheck();
  const landingQueryStringRef = useRef(null);

  // Capture entire query string on first land (sign-up page) so we can store with email
  useEffect(() => {
    if (location.search && landingQueryStringRef.current === null) {
      landingQueryStringRef.current = location.search;
    }
  }, [location.search]);

  // Get coupon code from URL query string
  const couponCode = searchParams.get('c');

  // Redirect authenticated users
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      if (!subscriptionLoading) {
        if (hasSubscription) {
          navigate('/');
        } else {
          // Preserve p, b, c (plan, billing, coupon) through to plans page
          const params = new URLSearchParams();
          const p = searchParams.get('p');
          const b = searchParams.get('b');
          if (couponCode) params.set('c', couponCode);
          if (p) params.set('p', p);
          if (b) params.set('b', b);
          const qs = params.toString();
          const plansUrl = qs ? `/plans?${qs}` : '/plans';
          navigate(plansUrl);
        }
      }
    }
  }, [isAuthenticated, authLoading, hasSubscription, subscriptionLoading, navigate, couponCode, searchParams]);

  const handleSignup = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      // Save email to database with status "new" (no auth required)
      const requestBody = { 
        email: email.trim(),
        name: null // We don't have the name yet
      };

      if (couponCode) {
        requestBody.coupon = couponCode;
      }
      const firstTouchQs = getFirstTouchQueryString();
      if (firstTouchQs) {
        requestBody.query_string = firstTouchQs;
      } else if (landingQueryStringRef.current) {
        requestBody.query_string = landingQueryStringRef.current;
      }
      
      const response = await fetchWithCSRF(`${process.env.REACT_APP_API_URL || '/api'}/members/ensure`, {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 409) {
          // Email already exists - redirect to login instead
          toast.error(`You already have an account with ${email}. Please log in instead.`);
          return;
        } else {
          console.error('Database save failed:', errorData);
          // Continue to Auth0 signup even if database save fails
        }
      }

      // Open Auth0 signup with pre-filled email
      await loginWithPopup({ 
        authorizationParams: { 
          screen_hint: 'signup',
          login_hint: email.trim()
        } 
      });
    } catch (error) {
      if (error.error === 'cancelled' || error.message === 'Popup closed') {
        // User cancelled, no need to show error
      } else {
        console.error('Signup error:', error);
        toast.error('There was an error with signup. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading while checking auth/subscription status
  if (authLoading || (isAuthenticated && subscriptionLoading)) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  // Don't render if authenticated (will redirect)
  if (isAuthenticated) {
    return null;
  }

  return (
    <div style={{ 
      padding: '4rem 2rem',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      maxWidth: '500px',
      margin: '0 auto'
    }}>
      <h1 style={{ 
        fontSize: '2rem', 
        fontWeight: '700', 
        color: '#333', 
        marginBottom: '1rem',
        textAlign: 'center'
      }}>
        Join Study Hall
      </h1>
      
      <p style={{ 
        fontSize: '1rem', 
        color: '#666', 
        marginBottom: '1rem',
        textAlign: 'center',
        lineHeight: '1.6'
      }}>
        Study Hall is a community and resource hub for freelance writers and journalists. We connect you with opportunities, provide industry insights, and help you grow your career.
      </p>
      
      <p style={{ 
        fontSize: '1rem', 
        color: '#666', 
        marginBottom: '2rem',
        textAlign: 'center',
        lineHeight: '1.6'
      }}>
        {couponCode ? (
          <>Get access to freelance opportunities, industry insights, and our community.</>
        ) : (
          <>Start your <span style={{ fontWeight: '700' }}>7-day free trial</span> and get access to freelance opportunities, industry insights, and our community.</>
        )}
      </p>

      <div style={{ 
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        {couponCode && (
          <p style={{ 
            color: '#000', 
            fontSize: '14px', 
            fontWeight: '700',
            margin: '0 0 0.5rem 0',
            textAlign: 'center'
          }}>
            Coupon {couponCode} will be applied
          </p>
        )}
        <input
          type="email"
          placeholder="Enter your email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !isSubmitting) {
              handleSignup();
            }
          }}
          style={{
            width: '100%',
            padding: '14px 16px',
            border: '2px solid #e9ecef',
            borderRadius: '6px',
            fontSize: '16px',
            transition: 'border-color 0.2s',
            boxSizing: 'border-box'
          }}
          onFocus={(e) => e.target.style.borderColor = '#38b44a'}
          onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
        />
        
        <button 
          onClick={handleSignup}
          disabled={isSubmitting}
          style={{
            width: '100%',
            backgroundColor: isSubmitting ? '#ccc' : '#38b44a',
            color: 'white',
            border: 'none',
            padding: '14px 24px',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => {
            if (!isSubmitting) {
              e.target.style.backgroundColor = '#2d8f3a';
            }
          }}
          onMouseOut={(e) => {
            if (!isSubmitting) {
              e.target.style.backgroundColor = '#38b44a';
            }
          }}
        >
          {isSubmitting ? 'Signing Up...' : 'Sign Up'}
        </button>
      </div>

      <div style={{ 
        marginTop: '2rem',
        textAlign: 'center'
      }}>
        <p style={{ 
          fontSize: '14px', 
          color: '#666',
          marginBottom: '0.5rem'
        }}>
          Already have an account?
        </p>
        <button
          onClick={() => navigate('/')}
          style={{
            backgroundColor: 'transparent',
            color: '#38b44a',
            border: 'none',
            padding: '8px 16px',
            fontSize: '14px',
            textDecoration: 'underline',
            cursor: 'pointer',
            transition: 'color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.color = '#2d8f3a'}
          onMouseOut={(e) => e.target.style.color = '#38b44a'}
        >
          Log In
        </button>
      </div>
    </div>
  );
}
